/** @format */

import React, { FC, useState, useMemo } from 'react'
import styled from 'styled-components'
import { _$, Dialog, Button, Icon, Radio, Transfer, Tip } from '@king-design/react'
import { TablePage } from 'src/components'
import useTableGroup from 'src/hooks/useTableGroup'
// eslint-disable-next-line max-len
import CloudListTableCopyTips from 'src/containers/MonitorCloud/CloudListTable/CloudListTableCopyTips'
import { useDeepCompareEffect } from 'react-use'
import { isEqual } from 'lodash'

interface DimensionDialogProps {
  className?: string
  values: string[]
  tags: string[]
  max?: number
  unmatch_instances: any[]
  onChange: (values: string[]) => void
}
const DimensionDialog: FC<DimensionDialogProps> = ({
  className,
  values,
  tags,
  max = 20,
  unmatch_instances,
  onChange
}) => {
  const [visible, setVisible] = useState<boolean>(false)
  const [transferValue, setTransferValue] = useState<string[]>([])
  // 未匹配资源提示
  const [unmatchVisible, setUnmatchVisible] = useState<boolean>(false)
  const [selectedGroup, setSelectedGroup] = useState<{ region: string }>({ region: '' })
  const regionGroup = useTableGroup('monitorRegionList')

  useDeepCompareEffect(() => {
    setTransferValue(values)
  }, [values])

  useDeepCompareEffect(() => {
    if (tags?.length === 1) {
      onChange(tags)
    } else if (tags?.length) {
      const filter = tags?.filter(item => values.includes(item)) || []
      onChange(filter)
    } else {
      onChange([])
    }
  }, [tags])

  // 筛选未匹配维度的资源
  const unmatch_table = useMemo(() => {
    const region = selectedGroup.region
    if (region) {
      const regionFilter = unmatch_instances.filter(instance => {
        return region ? instance.region === region : true
      })
      return regionFilter
    } else {
      return unmatch_instances
    }
  }, [unmatch_instances, selectedGroup.region])

  const scheme = {
    instanceName: {
      title: _$('实例名称'),
      template({ instanceName, instanceId }) {
        return (
          <div style={{ display: 'flex', alignContent: 'center' }}>
            <p style={{ marginBottom: 0 }} className="text-overflow">
              {instanceName}
            </p>
            <CloudListTableCopyTips instanceId={instanceId} />
          </div>
        )
      }
    },
    instanceIP: _$('IP地址'),
    region: {
      title: _$('region'),
      width: 200,
      group: regionGroup,
      template(item) {
        const find = regionGroup.find(({ value }) => item.region === value)
        return find?.label || item.region
      }
    },
    unselectedMetricTags: {
      title: _$('未匹配纬度'),
      template({ unselectedMetricTags }) {
        return unselectedMetricTags.join('、')
      }
    }
  }

  return (
    <div className={className}>
      <div>
        <Radio name="" trueValue="instanceRadio" value="instanceRadio">
          {_$('自选维度')}(<span className="primary-color">{values.length}</span>)
        </Radio>
      </div>
      <div className="select_button">
        <Button
          type="primary"
          onClick={() => {
            setVisible(true)
          }}
        >
          <Icon className="ion-plus-round" size="small" />
          {_$('选择维度')}
        </Button>
        {unmatch_instances?.length ? (
          <p
            className="unmatch_tip"
            onClick={() => {
              setUnmatchVisible(true)
            }}
          >
            资源未匹配维度({unmatch_instances?.length})
          </p>
        ) : null}
      </div>
      <Dialog
        value={visible}
        width={610}
        disabledOk={!transferValue?.length || transferValue?.length > max}
        onChangeValue={show => {
          if (!show) {
            setVisible(false)
            setTransferValue(values)
          }
        }}
        onOk={() => {
          onChange(transferValue)
        }}
        title={_$('维度选择')}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Tip type="warning">当前资源监控项维度最大支持勾选{max}个</Tip>
          </div>
          <Transfer
            data={tags.map(item => ({ label: item, key: item }))}
            filter={(data, keywords) => {
              return data.key.includes(keywords)
            }}
            filterable
            keyName="key"
            value={transferValue}
            onChangeValue={(value, oldValue) => {
              const equal = isEqual(value, oldValue)
              !equal && setTransferValue(value)
            }}
          ></Transfer>
        </div>
      </Dialog>
      <Dialog
        value={unmatchVisible}
        onOk={() => {
          setUnmatchVisible(false)
        }}
        onChangeValue={show => {
          if (!show) {
            setUnmatchVisible(false)
            setSelectedGroup({ region: '' })
          }
        }}
        title={_$('资源未匹配维度')}
      >
        <div style={{ marginBottom: 16 }}>
          <Tip type="warning">资源实例存在未匹配的纬度信息</Tip>
        </div>
        <TablePage
          tableProps={{
            scheme: scheme,
            data: unmatch_table,
            checkType: 'none',
            group: selectedGroup,
            onChangeGroup: value => {
              setSelectedGroup(value)
            },
            rowKey: value => value.instanceId
          }}
        ></TablePage>
      </Dialog>
    </div>
  )
}

export default styled(DimensionDialog)`
  margin-top: 6px;
  .select_button {
    display: flex;
    margin-top: 16px;
    align-items: center;
  }
  .unmatch_tip {
    color: #ff4133;
    margin: 0 0 0 16px;
    cursor: pointer;
  }
`
